import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import MenuAtom, { MenuItem, MenuHandle } from '../../atoms/menu/menu';
import StatusBadgeAtom, { StatusBadgeStatus } from '../../atoms/StatusBadge/StatusBadgeAtom';
import { Sphere } from 'types/global';
import { RootState } from '@center-pwa/store/store';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useSpherePermission } from '../../../hooks/useSpherePermission';
import { useSphereSurfaces } from '../../../hooks/useSphereSurfaces';
import './MetaspaceMenu.scss';

interface MetaspaceMenuProps {
  sphere: Sphere;
  perspectiveSystemName?: string;
  metaspaceSelected?: (systemName: string) => void;
  className?: string;
  isNavBar?: boolean;
  onOpen?: () => void;
  onAddMetaspaceClick?: () => void;
  /**
   * Explicit Space order override. When omitted, the menu resolves its own
   * `spaceOrder` from the surface-bindings resolver. Matched Spaces sort by
   * their index in this array; unmatched Spaces are appended alphabetically.
   * An empty/unresolved order = the existing fully-alphabetical behaviour.
   */
  order?: string[];
}

const MetaspaceMenu: React.FC<MetaspaceMenuProps> = ({
  sphere,
  perspectiveSystemName,
  metaspaceSelected,
  className,
  isNavBar,
  onOpen,
  onAddMetaspaceClick,
  order,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { allowed: canAddMetaspace } = useSpherePermission(sphere.id, 'edit');
  // Resolved here rather than drilled through every caller (metasphere-nav-bar
  // and MobileHeaderOrganism both render this menu): one hook instance keeps a
  // single /surfaces request per mounted menu, and the `order` prop stays as an
  // explicit override for callers that already hold a resolved order.
  const { spaceOrder } = useSphereSurfaces(apiClients, sphere.id);
  const effectiveOrder = order ?? spaceOrder;
  const isMobile = useMediaQuery('(max-width: 500px)');
  const menuRef = useRef<MenuHandle>(null);
  // Raw fetched rows, not the built MenuItems: `order` can arrive AFTER the
  // menu is first opened (useSphereSurfaces resolves asynchronously), and
  // sorting inside the one-shot fetch would leave a late order unapplied.
  // Deriving the items keeps the menu re-sorting as `order` settles.
  const [fetchedMetaspaces, setFetchedMetaspaces] = useState<any[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedMetaspaceName, setSelectedMetaspaceName] =
    useState<string>('');

  const { systemName } = useParams<{
    sphereId: string;
    systemName: string;
  }>();

  const metaspaces = useSelector(selectAllMetaspaces);

  const metaspaceFromRoute = metaspaces.find(
    (metaspace) => metaspace.system_name === systemName
  );

  useEffect(() => {
    setFetchedMetaspaces(null);
    if (metaspaceFromRoute?.name) {
      setSelectedMetaspaceName(metaspaceFromRoute.name);
    } else {
      setSelectedMetaspaceName('');
    }
  }, [sphere, metaspaceFromRoute?.name, isMobile]);

  useEffect(() => {
    const handler = () => (menuRef.current as any)?.hide({ currentTarget: document.body });
    document.addEventListener('atlas:close-metaspace-menus', handler);
    return () => document.removeEventListener('atlas:close-metaspace-menus', handler);
  }, []);

  const menuItems: MenuItem[] = useMemo(() => {
    if (!fetchedMetaspaces) return [];

    const orderIndex = new Map((effectiveOrder ?? []).map((name, index) => [name, index]));
    const alphabetically = (a: any, b: any) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

    const visibleMetaspaces = (isMobile
      ? fetchedMetaspaces.filter((m) => m.mobile_enabled !== false)
      : fetchedMetaspaces
    )
      .slice()
      .sort((a, b) => {
        if (orderIndex.size === 0) return alphabetically(a, b);
        const aIndex = orderIndex.get(a.system_name);
        const bIndex = orderIndex.get(b.system_name);
        if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
        if (aIndex !== undefined) return -1;
        if (bIndex !== undefined) return 1;
        return alphabetically(a, b);
      });

    const sourceToStatus = (
      source?: string,
      isForked?: boolean
    ): StatusBadgeStatus | null => {
      if (isForked) return 'fork';
      if (source === 'approach') return 'approach';
      if (source === 'channel') return 'channel';
      if (source === 'manual') return 'manual';
      return null;
    };

    return visibleMetaspaces.map((metaspace) => {
      const handleItemClick = () => {
        setSelectedMetaspaceName(metaspace.name);
        metaspaceSelected?.(metaspace.system_name);
      };

      const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleItemClick();
        }
      };

      const isSelected =
        metaspace.name === selectedMetaspaceName ||
        metaspace.system_name === systemName;

      const sourceBadgeStatus = sourceToStatus(metaspace.source, metaspace.is_forked);
      const sourceBadgeLabel = sourceBadgeStatus
        ? t(`sphereMetaspaces.source.${sourceBadgeStatus}`)
        : null;

      return {
        template: () => (
          <div
            className={`flex align-items-center gap-2 metaspace-menu-item${isSelected ? ' metaspace-menu-item--selected' : ''}`}
            style={{ cursor: 'pointer' }}
            role="menuitem"
            tabIndex={0}
            onClick={handleItemClick}
            onKeyDown={handleKeyDown}
            aria-label={metaspace.name}
          >
            <i className="metaspace-icon"></i>
            <span className="metaspace-menu-item__name">{metaspace.name}</span>
            {sourceBadgeStatus && sourceBadgeLabel && (
              <StatusBadgeAtom
                status={sourceBadgeStatus}
                label={sourceBadgeLabel}
                size="small"
                className="metaspace-menu-item__source-badge"
              />
            )}
          </div>
        ),
        command: handleItemClick,
      };
    });
  }, [
    fetchedMetaspaces,
    effectiveOrder,
    isMobile,
    selectedMetaspaceName,
    systemName,
    metaspaceSelected,
    t,
  ]);

  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;

  const handleClick = async (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement;
    const button = target.closest('.metaspace-selector');
    if (button) {
      button.classList.add('ripple-active');
      setTimeout(() => button.classList.remove('ripple-active'), 600);
    }

    const anchor = e;
    onOpen?.();
    document.dispatchEvent(new CustomEvent('atlas:close-edit-pills'));
    menuRef.current?.toggle(anchor);

    if (fetchedMetaspaces !== null || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await graphClient.get<{ metaspaces: any[] }>(
        `/spheres/${encodeURIComponent(sphere.id)}/metaspaces?include=basic`
      );
      setFetchedMetaspaces(res.metaspaces ?? []);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch metaspaces.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const displayName =
    selectedMetaspaceName.trim().length > 0
      ? selectedMetaspaceName
      : metaspaceFromRoute?.name || '';

  const footerItems: MenuItem[] =
    canAddMetaspace && onAddMetaspaceClick
      ? [
          { separator: true },
          {
            template: () => (
              <div
                className="metaspace-menu-add-item"
                role="menuitem"
                tabIndex={0}
                onClick={() => {
                  (menuRef.current as any)?.hide({ currentTarget: document.body });
                  onAddMetaspaceClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    (menuRef.current as any)?.hide({ currentTarget: document.body });
                    onAddMetaspaceClick();
                  }
                }}
              >
                <i className="pi pi-plus" />
                <span>{t('sphereMetaspaces.addMetaspace.menuItem')}</span>
              </div>
            ),
          },
        ]
      : [];

  const fullMenuModel: MenuItem[] = [...menuItems, ...footerItems];

  return (
    <div className={`metaspace-selector ${className ?? ''}`}>
      <div
        onClick={handleClick}
        onMouseDown={() => document.dispatchEvent(new CustomEvent('atlas:close-edit-pills'))}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <div style={{ cursor: 'pointer', position: 'relative' }}>
          <i className={`metaspace-icon ${loading ? 'pulsing' : ''}`}></i>
        </div>
        {displayName && isNavBar && (
          <span className="selected-metaspace-name">{displayName}</span>
        )}
      </div>
      <MenuAtom
        model={fullMenuModel}
        popup
        ref={menuRef}
        appendTo={document.body}
        className="custom-dropdown-menu"
      />
      {error && <div style={{ color: 'red', marginTop: '8px' }}>{error}</div>}
    </div>
  );
};

export default MetaspaceMenu;
